import React, { useState, useEffect } from 'react'
import { Steps, Form, Input, message } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import * as API from '../../../../api'
import * as UTILS from '../../../../utils'
import './index.less'

export interface ResetPasswordFormProps {
	goLogin?: () => void;
}

const { Step } = Steps

const steps = [
	{ title: '身份验证' },
	{ title: '重置密码' },
]

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
	goLogin,
}) => {
	const [current, setCurrent] = useState<number>(0)

	const [getVercodeEnabled, setGetVerCodeEnabled] = useState<boolean>(false)
	const [count, setCount] = useState<number>(60)
	const [isSend, setIsSend] = useState<boolean>(false) // 是否已经发送验证码

	const [validateForm] = Form.useForm()
	const [pwdForm] = Form.useForm()

	const [submitEnabled, setSubmitEnabled] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [errorTips, setErrorTips] = useState<string>('')

	const goToLogin = () => {
		goLogin && goLogin()
	}

	const nextStep = async () => {
		if (current === (steps.length - 1)) return
		setCurrent(current + 1)
		setSubmitEnabled(false)
	}

	const getVerCode = async () => {
		const userPhone = validateForm.getFieldValue('userPhone')
		try {
			setIsSend(true)
			const { code, msg } = await API.Login.sendCode({ userPhone })
			if (code === 0 || code === 10030) {
				message.info(msg || '验证码已发送')
			} else {
				message.error(msg)
				setIsSend(false)
			}
		} catch (error) {
			message.error('验证码发送失败')
			setIsSend(false)
		}
	}

	useEffect(() => {
		let timer
		if (isSend && count) {
			timer = setInterval(() => {
				setCount(n => {
					if (n === 0) {
						setIsSend(false)
						clearInterval(timer)
					}
					return n - 1
				})
			}, 1000)
		} else {
			setCount(60)
		}
		return () => clearInterval(timer)
	}, [isSend])

	const formValidate = (form) => {
		if (!form.isFieldsTouched()) return
		setGetVerCodeEnabled(/^1[3-9]\d{9}$/.test(form.getFieldValue('userPhone')))
		setTimeout(() => {
			const objValidate = UTILS.objValidate(form.getFieldsValue())
			const errors = form.getFieldsError().filter(item => item.errors.length > 0)
			setSubmitEnabled(objValidate && errors.length === 0)
		}, 0)
	}

	const onSubmit = async () => {
		if (!submitEnabled) return
		if (current === 0) {
			// 重置密码第一步
			const fieldsValue = validateForm.getFieldsValue()
			try {
				setLoading(true)
				const { code, msg } = await API.Login.checkPhoneAndCode(fieldsValue)
				setLoading(false)
				if (code === 0) {
					nextStep()
				} else {
					message.error(msg)
				}
			} catch (error) {
				message.error('验证失败')
				setLoading(false)
			}
		} else {
			// 重置密码第二步
			const value = pwdForm.getFieldsValue()
			try {
				setLoading(true)
				const { code, msg } = await API.Login.resetPassword({ ...value, userPhone: validateForm.getFieldValue('userPhone') })
				setLoading(false)
				if (code === 0) {
					message.success('重置密码成功')
					goToLogin()
				} else {
					message.error(msg)
				}
			} catch (error) {
				message.error('重置密码失败')
				setLoading(false)
			}
		}
	}

	return (
		<div className="reset-pwd-form">
			<Steps size="small" current={current}>
				{steps.map((step, i) => <Step key={i} title={step.title} />)}
			</Steps>

			<Form form={validateForm} onValuesChange={() => formValidate(validateForm)} style={current === 0 ? { display: 'block' } : { display: 'none' }}>
				<Form.Item name="userPhone" rules={[
					{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
				]}>
					<Input placeholder="手机号" autoComplete="off"/>
				</Form.Item>

				<Form.Item name="checkCode">
					<Input placeholder="请输入验证码" autoComplete="off"
						suffix={
							getVercodeEnabled
								? (isSend
									? <div className="vercode-btn waiting">重新获取{count}s</div>
									: <div className="vercode-btn active" onClick={getVerCode}>获取验证码</div>
								)
								: <div className="vercode-btn">获取验证码</div>
						}
					/>
				</Form.Item>
			</Form>

			<Form form={pwdForm} onValuesChange={() => formValidate(pwdForm)} style={current === 1 ? { display: 'block' } : { display: 'none' }}>
				<Form.Item name="password1" rules={[
					{ required: true, message: '请输入登录密码' },
				]}>
					{/* TODO 密码校验 */}
					<Input type="password" placeholder="请设置登录密码，8位数字或字母混合" />
				</Form.Item>

				<Form.Item name="password2" dependencies={['password1']} rules={[
					{ required: true, message: '请再次输入密码' },
					({ getFieldValue }) => ({
						validator(rule, value) {
							if (!value || getFieldValue('password1') === value) {
								return Promise.resolve()
							}
							return Promise.reject('两次输入的密码不一致')
						},
					}),
				]}>
					<Input type="password" placeholder="请再次输入密码" />
				</Form.Item>
			</Form>

			<div className={submitEnabled ? 'submit-button active' : 'submit-button'} onClick={onSubmit}
				style={loading ? { boxShadow: 'inset 0 0 1000px rgba(255, 255, 255, 0.3)' } : {}}
			>
				{loading && <LoadingOutlined />}
				{current === 0 ? '下一步' : '完成'}
			</div>

			<div className="extra">
				<span className="error-tips">{errorTips}</span>
			</div>
			<div style={{ textAlign: 'center' }}>
				<span className="link" onClick={goToLogin}>返回登录</span>
			</div>
		</div>
	)
}

export default ResetPasswordForm
